/**
 * Deterministic service-rules engine (spec §7).
 *
 * Pure functions only: no I/O, no clock reads, no AI. Every decision takes an
 * explicit rule-set version and explicit timestamps so results are
 * reproducible and auditable. ServeIQ / RouteIQ may recommend, but can never
 * override the answers produced here.
 */
import {
  IMMEDIATE_NON_SERVICE_OUTCOMES,
  UNSUCCESSFUL_OUTCOMES,
  type AttemptOutcome,
  type Issue,
  type NonServicePolicy,
  type PaperTypeRules,
  type RuleSetConfig,
  type ServiceMethod,
  type TimeWindow,
} from './types.ts';
import { dayNumber, fullDaysBetween, hhmmToMinutes, HOUR_MS, localParts, type LocalParts } from '../time.ts';

// ─── Inputs ────────────────────────────────────────────────────────────────

export interface AttemptRecord {
  id: string;
  attemptedAt: Date;
  outcome: AttemptOutcome;
  method?: ServiceMethod | null;
  /** Supervisor override was used to attempt on a Sunday/holiday. */
  calendarOverride?: boolean;
  /** Voided/superseded attempts never count. */
  voided?: boolean;
}

export type PaperPhase = 'open' | 'served' | 'non_served' | 'pending_review' | 'closed';

export interface PaperRuleContext {
  paperTypeCode: string;
  phase: PaperPhase;
  onHold: boolean;
  recalled: boolean;
  attempts: AttemptRecord[];
}

export interface ProposedAttempt {
  at: Date;
  method?: ServiceMethod | null;
  outcome?: AttemptOutcome;
  calendarOverride?: boolean;
}

export interface SubstituteRecipient {
  name?: string;
  relationship?: string;
  age?: number | null;
  estimatedAge?: number | null;
  residesWithDefendant?: boolean;
  competent?: boolean;
  notAParty?: boolean;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

export class RuleConfigError extends Error {}

export function getPaperType(rules: RuleSetConfig, code: string): PaperTypeRules {
  const t = rules.paperTypes.find((p) => p.code === code);
  if (!t) throw new RuleConfigError(`Unknown paper type "${code}" in rule set`);
  return t;
}

export function isUnsuccessful(o: AttemptOutcome): boolean {
  return UNSUCCESSFUL_OUTCOMES.includes(o);
}

export function isImmediateDisposition(t: PaperTypeRules, o: AttemptOutcome): boolean {
  return IMMEDIATE_NON_SERVICE_OUTCOMES.includes(o) && t.immediateDispositions.includes(o);
}

function holidayOn(rules: RuleSetConfig, date: string) {
  return rules.holidays.find((h) => h.date === date);
}

function inWindow(lp: LocalParts, w: TimeWindow): boolean {
  return lp.minuteOfDay >= hhmmToMinutes(w.start) && lp.minuteOfDay < hhmmToMinutes(w.end);
}

export function isWithinServiceHours(rules: RuleSetConfig, at: Date): boolean {
  const lp = localParts(at, rules.timezone);
  return lp.minuteOfDay >= hhmmToMinutes(rules.serviceHours.start) && lp.minuteOfDay < hhmmToMinutes(rules.serviceHours.end);
}

/** Calendar blockers (Sunday / holiday) for a moment in agency local time. */
export function calendarBlocks(rules: RuleSetConfig, at: Date): Issue[] {
  const lp = localParts(at, rules.timezone);
  const out: Issue[] = [];
  if (rules.sundayBlocked && lp.weekday === 0) {
    out.push({ code: 'SUNDAY_BLOCKED', message: 'Sunday service is blocked; supervisor override required.' });
  }
  const h = holidayOn(rules, lp.date);
  if (h) out.push({ code: 'HOLIDAY_BLOCKED', message: `${h.name} is a blocked holiday; supervisor override required.` });
  return out;
}

/**
 * An attempt that may be counted toward a non-service / posting requirement.
 * Excludes voided attempts, non-unsuccessful outcomes, and (unless configured)
 * attempts made on blocked days under supervisor override.
 */
function isCountableUnsuccessful(rules: RuleSetConfig, a: AttemptRecord): boolean {
  if (a.voided || !isUnsuccessful(a.outcome)) return false;
  if (calendarBlocks(rules, a.attemptedAt).length > 0 && !rules.overrideAttemptsQualify) return false;
  return true;
}

// ─── Eviction (RULE-007) ───────────────────────────────────────────────────

export interface EvictionProgress {
  attempt1?: AttemptRecord;
  attempt2?: AttemptRecord;
  /** Earliest local date (YYYY-MM-DD) on which posting / attempt 3 may occur. */
  postingEligibleFromDate?: string;
  notes: string[];
}

export function evictionProgress(rules: RuleSetConfig, type: PaperTypeRules, attempts: AttemptRecord[]): EvictionProgress {
  const policy = type.eviction;
  if (!policy) return { notes: ['Paper type has no eviction policy.'] };
  const candidates = attempts.filter((a) => isCountableUnsuccessful(rules, a)).sort((a, b) => +a.attemptedAt - +b.attemptedAt);
  const attempt1 = candidates[0];
  if (!attempt1) return { notes: ['Attempt 1 not yet made.'] };
  const lp1 = localParts(attempt1.attemptedAt, rules.timezone);
  const notes: string[] = [];
  let attempt2: AttemptRecord | undefined;
  for (const c of candidates.slice(1)) {
    const hours = (+c.attemptedAt - +attempt1.attemptedAt) / HOUR_MS;
    const lp = localParts(c.attemptedAt, rules.timezone);
    const todDiffH = Math.abs(lp.minuteOfDay - lp1.minuteOfDay) / 60;
    if (hours >= policy.minHoursBetweenAttempt1And2 && todDiffH >= policy.minTimeOfDayDifferenceHours) {
      attempt2 = c;
      break;
    }
    notes.push(
      `Attempt on ${lp.date} does not qualify as attempt 2 (needs ≥${policy.minHoursBetweenAttempt1And2}h after attempt 1 and ≥${policy.minTimeOfDayDifferenceHours}h different time of day).`,
    );
  }
  if (!attempt2) return { attempt1, notes: [...notes, 'Qualifying attempt 2 not yet made.'] };
  const lp2 = localParts(attempt2.attemptedAt, rules.timezone);
  const d = new Date(Date.UTC(lp2.year, lp2.month - 1, lp2.day + policy.postingMinDaysAfterAttempt2));
  return { attempt1, attempt2, postingEligibleFromDate: d.toISOString().slice(0, 10), notes };
}

/** Would a new unsuccessful attempt at `at` qualify as eviction attempt 2? */
function evictionAttempt2Warning(rules: RuleSetConfig, type: PaperTypeRules, attempts: AttemptRecord[], at: Date): Issue | null {
  const policy = type.eviction;
  if (!policy) return null;
  const prog = evictionProgress(rules, type, attempts);
  if (!prog.attempt1 || prog.attempt2) return null;
  const hours = (+at - +prog.attempt1.attemptedAt) / HOUR_MS;
  const tod = Math.abs(localParts(at, rules.timezone).minuteOfDay - localParts(prog.attempt1.attemptedAt, rules.timezone).minuteOfDay) / 60;
  const reasons: string[] = [];
  if (hours < policy.minHoursBetweenAttempt1And2) reasons.push(`only ${hours.toFixed(1)}h after attempt 1 (need ${policy.minHoursBetweenAttempt1And2}h)`);
  if (tod < policy.minTimeOfDayDifferenceHours) reasons.push(`time of day differs by ${tod.toFixed(1)}h (need ${policy.minTimeOfDayDifferenceHours}h)`);
  if (!reasons.length) return null;
  return { code: 'EVICTION_ATTEMPT2_TIMING', message: `If unsuccessful, this will not count as eviction attempt 2: ${reasons.join('; ')}.` };
}

// ─── Regular non-service (RULE-004 / RULE-005) ─────────────────────────────

export interface NonServiceProgress {
  satisfied: boolean;
  basis: 'attempts' | 'immediate_disposition' | null;
  requiresSupervisorReview: boolean;
  /** Attempt ids forming a qualifying set, when satisfied by attempts. */
  qualifyingAttemptIds: string[];
  /** Window id → attempt id used (may be 'saturday_wildcard'). */
  windowCoverage: Record<string, { attemptId: string; viaSaturdayWildcard: boolean }>;
  countableAttempts: number;
  missing: string[];
}

interface Candidate {
  a: AttemptRecord;
  lp: LocalParts;
}

const MAX_CANDIDATES = 40;


// ─── M / A / E / W window tracker (what the deputy sees as four circles) ───

export interface WindowSlot {
  /** Window id ('morning' …) or 'saturday'. */
  id: string;
  letter: string;
  label: string;
  attemptId: string | null;
  at: Date | null;
}

export interface WindowTracker {
  slots: WindowSlot[];
  green: number;
  /** Circles needed (3 of the 4 with the Saturday slot). */
  needed: number;
  satisfied: boolean;
  countable: number;
}

const LETTERS: Record<string, string> = { morning: 'M', afternoon: 'A', evening: 'E' };

/**
 * Best assignment of countable attempts to the time-window circles.
 * Window circles (M/A/E) need attempts at least `minFullDaysBetween` full days apart.
 * The Saturday circle (W) takes a Saturday attempt at any time and needs no spacing.
 * Null for paper types without a regular non-service policy (e.g. Rule to Vacate).
 */
export function windowTracker(rules: RuleSetConfig, type: PaperTypeRules, attempts: AttemptRecord[]): WindowTracker | null {
  const policy = type.nonService;
  if (!policy) return null;
  const cands: Candidate[] = attempts
    .filter((a) => isCountableUnsuccessful(rules, a))
    .sort((x, y) => +x.attemptedAt - +y.attemptedAt)
    .slice(-12)
    .map((a) => ({ a, lp: localParts(a.attemptedAt, rules.timezone) }));
  const wild = policy.saturdayWildcard.enabled && policy.saturdayWildcard.maxCount > 0;
  const slotDefs = [
    ...policy.windows.map((w) => ({ id: w.id, letter: LETTERS[w.id] ?? w.label.charAt(0).toUpperCase(), label: w.label, window: w as TimeWindow | null })),
    ...(wild ? [{ id: 'saturday', letter: 'W', label: 'Saturday (any time)', window: null as TimeWindow | null }] : []),
  ];
  let best: (Candidate | null)[] = slotDefs.map(() => null);
  let bestN = 0;
  const pick: (Candidate | null)[] = [];
  const used = new Set<number>();
  const search = (si: number, n: number) => {
    if (n + (slotDefs.length - si) <= bestN) return;
    if (si === slotDefs.length) {
      if (n > bestN) {
        bestN = n;
        best = [...pick];
      }
      return;
    }
    const def = slotDefs[si]!;
    for (let i = 0; i < cands.length; i++) {
      if (used.has(i)) continue;
      const c = cands[i]!;
      if (def.window) {
        if (!inWindow(c.lp, def.window)) continue;
        const spaced = pick.every((o, k) => !o || !slotDefs[k]!.window || Math.abs(dayNumber(o.lp.date) - dayNumber(c.lp.date)) - 1 >= policy.minFullDaysBetween);
        if (!spaced) continue;
      } else if (c.lp.weekday !== 6) continue;
      used.add(i);
      pick.push(c);
      search(si + 1, n + 1);
      pick.pop();
      used.delete(i);
    }
    pick.push(null);
    search(si + 1, n);
    pick.pop();
  };
  search(0, 0);
  const slots = slotDefs.map((d, i) => ({ id: d.id, letter: d.letter, label: d.label, attemptId: best[i]?.a.id ?? null, at: best[i]?.a.attemptedAt ?? null }));
  const needed = policy.windows.length;
  return { slots, green: bestN, needed, satisfied: bestN >= needed && cands.length >= policy.minAttempts, countable: cands.length };
}

/** Would an unsuccessful attempt at `at` turn a new circle green? If not, why. */
export function windowWarning(rules: RuleSetConfig, type: PaperTypeRules, attempts: AttemptRecord[], at: Date): Issue | null {
  const before = windowTracker(rules, type, attempts);
  if (!before || before.satisfied) return null;
  const probe: AttemptRecord = { id: '__probe__', attemptedAt: at, outcome: 'no_answer', method: null, voided: false } as AttemptRecord;
  if (!isCountableUnsuccessful(rules, probe)) return null;
  const after = windowTracker(rules, type, [...attempts, probe])!;
  if (after.green > before.green) return null;
  const lp = localParts(at, rules.timezone);
  const policy = type.nonService!;
  const win = policy.windows.find((w) => inWindow(lp, w));
  const sameSlot = win && before.slots.find((s) => s.id === win.id)?.attemptId;
  const satDone = lp.weekday === 6 && before.slots.find((s) => s.id === 'saturday')?.attemptId;
  const close = attempts
    .filter((a) => isCountableUnsuccessful(rules, a))
    .map((a) => localParts(a.attemptedAt, rules.timezone).date)
    .find((d) => Math.abs(dayNumber(d) - dayNumber(lp.date)) - 1 < policy.minFullDaysBetween);
  let why: string;
  let code = 'WINDOW_ALREADY_COVERED';
  if (!win && lp.weekday !== 6) why = 'it is outside the morning, afternoon and evening windows';
  else if (sameSlot && (lp.weekday !== 6 || satDone)) why = `there is already a ${win!.label.toLowerCase()} attempt`;
  else if (close) {
    why = `it is less than ${policy.minFullDaysBetween === 1 ? 'a full day' : `${policy.minFullDaysBetween} full days`} after the attempt on ${close}`;
    code = 'ATTEMPT_SPACING';
  } else why = 'it does not fill a new time window';
  return { code, message: `Non-qualified attempt: you can log it, but it will not count toward non-service: ${why}. Non-service needs 3 of the 4 circles (M, A, E, W) green.` };
}

// ─── Rule to Vacate status (instead of circles) ──────────────────────────────

export interface VacateStatus {
  stage: 'first_attempt' | 'second_attempt' | 'post';
  /** Earliest moment the next qualifying attempt / posting may happen. */
  nextFrom: Date | null;
  /** Human text, e.g. "Next eligible attempt: Wed 9/30, 6:00 PM – 10:00 PM". */
  text: string;
  eligibleNow: boolean;
}

const fmtDay = (d: string) => {
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  const w = new Date(Date.UTC(y, m - 1, dd)).getUTCDay();
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][w]} ${m}/${dd}`;
};
const fmtMin = (min: number) => {
  const h = Math.floor(min / 60);
  const mm = min % 60;
  return `${((h + 11) % 12) + 1}:${String(mm).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * Eviction papers (Rule to Vacate): attempts 1 and 2 must be ≥48 h apart AND ≥8 h different time
 * of day; after that, posting / attempt 3 is allowed from the next day.
 */
export function vacateStatus(rules: RuleSetConfig, type: PaperTypeRules, attempts: AttemptRecord[], now: Date): VacateStatus | null {
  const policy = type.eviction;
  if (!policy) return null;
  const prog = evictionProgress(rules, type, attempts);
  const nowLp = localParts(now, rules.timezone);
  const openS = hhmmToMinutes(rules.serviceHours.start);
  const closeS = hhmmToMinutes(rules.serviceHours.end);
  const blocked = (date: string) => (rules.sundayBlocked && localParts(new Date(`${date}T12:00:00Z`), 'UTC').weekday === 0) || !!holidayOn(rules, date);
  if (prog.postingEligibleFromDate) {
    const d = prog.postingEligibleFromDate;
    const ok = nowLp.date >= d;
    return { stage: 'post', nextFrom: null, eligibleNow: ok, text: ok ? 'Eligible to post now' : `Eligible to post from ${fmtDay(d)}` };
  }
  if (!prog.attempt1) return { stage: 'first_attempt', nextFrom: null, eligibleNow: true, text: 'Eligible for service' };
  // Find the first allowed day/time window for attempt 2.
  const a1 = prog.attempt1.attemptedAt;
  const lp1 = localParts(a1, rules.timezone);
  const earliest = +a1 + policy.minHoursBetweenAttempt1And2 * HOUR_MS;
  const gap = policy.minTimeOfDayDifferenceHours * 60;
  for (let k = 0; k < 14; k++) {
    const date = new Date(Date.UTC(lp1.year, lp1.month - 1, lp1.day + Math.floor(policy.minHoursBetweenAttempt1And2 / 24) + k)).toISOString().slice(0, 10);
    if (blocked(date)) continue;
    const ranges: [number, number][] = [];
    const lowEnd = Math.min(lp1.minuteOfDay - gap, closeS);
    if (lowEnd > openS) ranges.push([openS, lowEnd]);
    const highStart = Math.max(lp1.minuteOfDay + gap, openS);
    if (highStart < closeS) ranges.push([highStart, closeS]);
    // On the first possible day nothing before the 48-hour mark counts.
    const firstDay = k === 0;
    const usable = ranges
      .map(([a, b]) => (firstDay ? ([Math.max(a, lp1.minuteOfDay), b] as [number, number]) : ([a, b] as [number, number])))
      .filter(([a, b]) => a < b);
    if (!usable.length) continue;
    const text = `Next eligible attempt: ${fmtDay(date)}, ${usable.map(([a, b]) => `${fmtMin(a)} – ${fmtMin(b)}`).join(' or ')}`;
    const nowMin = nowLp.minuteOfDay;
    const eligibleNow = +now >= earliest && Math.abs(nowMin - lp1.minuteOfDay) >= gap && nowMin >= openS && nowMin < closeS && !blocked(nowLp.date);
    const startMin = usable[0]![0];
    const nextFrom = new Date(Date.parse(`${date}T00:00:00Z`) + startMin * 60_000);
    return { stage: 'second_attempt', nextFrom, eligibleNow, text: eligibleNow ? `Eligible now for attempt 2 (${text.replace('Next eligible attempt: ', 'window ')})` : text };
  }
  return { stage: 'second_attempt', nextFrom: null, eligibleNow: false, text: 'Attempt 2 window not found in the next two weeks — check holidays and service hours.' };
}

export function nonServiceProgress(rules: RuleSetConfig, type: PaperTypeRules, attempts: AttemptRecord[]): NonServiceProgress {
  const live = attempts.filter((a) => !a.voided);
  const base: NonServiceProgress = {
    satisfied: false,
    basis: null,
    requiresSupervisorReview: false,
    qualifyingAttemptIds: [],
    windowCoverage: {},
    countableAttempts: 0,
    missing: [],
  };

  const immediate = live.find((a) => isImmediateDisposition(type, a.outcome));
  if (immediate) {
    return { ...base, satisfied: true, basis: 'immediate_disposition', qualifyingAttemptIds: [immediate.id] };
  }
  if (live.some((a) => type.supervisorReviewDispositions.includes(a.outcome))) {
    base.requiresSupervisorReview = true;
    base.missing.push('Unable-to-locate disposition requires supervisor review.');
  }

  const policy = type.nonService;
  if (!policy) {
    base.missing.push(`${type.name}: non-service requires an immediate disposition or supervisor review.`);
    return base;
  }

  const candidates: Candidate[] = live
    .filter((a) => isCountableUnsuccessful(rules, a))
    .sort((x, y) => +x.attemptedAt - +y.attemptedAt)
    .slice(-MAX_CANDIDATES)
    .map((a) => ({ a, lp: localParts(a.attemptedAt, rules.timezone) }));
  base.countableAttempts = candidates.length;

  const k = Math.max(policy.minAttempts, policy.windows.length);
  const tr = windowTracker(rules, type, attempts)!;
  if (tr.satisfied) {
    const cov: NonServiceProgress['windowCoverage'] = {};
    const sat = tr.slots.find((x) => x.id === 'saturday' && x.attemptId);
    for (const w of policy.windows) {
      const sl = tr.slots.find((x) => x.id === w.id);
      if (sl?.attemptId) cov[w.id] = { attemptId: sl.attemptId, viaSaturdayWildcard: false };
      else if (sat) cov[w.id] = { attemptId: sat.attemptId!, viaSaturdayWildcard: true };
    }
    return { ...base, satisfied: true, basis: 'attempts', qualifyingAttemptIds: tr.slots.filter((x) => x.attemptId).map((x) => x.attemptId!), windowCoverage: cov, missing: [] };
  }

  // Explain what is missing.
  if (candidates.length < k) base.missing.push(`Needs ${k} qualifying in-person attempts; ${candidates.length} so far.`);
  const hasSaturday = policy.saturdayWildcard.enabled && candidates.some((c) => c.lp.weekday === 6);
  const uncovered = policy.windows.filter((w) => !candidates.some((c) => inWindow(c.lp, w)));
  if (uncovered.length) {
    const wildNote = hasSaturday && uncovered.length === 1 ? ' (a Saturday attempt may substitute for one window)' : '';
    base.missing.push(`No attempt yet in: ${uncovered.map((w) => w.label).join(', ')}${wildNote}.`);
  }
  if (candidates.length >= k && (uncovered.length === 0 || (uncovered.length === 1 && hasSaturday))) {
    base.missing.push(
      `Needs ${k} attempts, each separated by at least ${policy.minFullDaysBetween} full day(s), covering every time window with a different attempt (max ${policy.saturdayWildcard.maxCount} Saturday wildcard).`,
    );
  }
  return base;
}

// ─── Attempt evaluation ────────────────────────────────────────────────────

export interface AttemptEvaluation {
  /** Hard rule result: may this attempt be recorded / finalized? */
  allowed: boolean;
  blockers: Issue[];
  warnings: Issue[];
  /** Outside permitted hours — allowed; explanation is optional (RULE-002). */
  outsideServiceHours: boolean;
}

export function evaluateAttempt(rules: RuleSetConfig, paper: PaperRuleContext, proposed: ProposedAttempt): AttemptEvaluation {
  const type = getPaperType(rules, paper.paperTypeCode);
  const blockers: Issue[] = [];
  const warnings: Issue[] = [];

  if (paper.phase !== 'open') blockers.push({ code: 'PAPER_NOT_OPEN', message: `Paper is ${paper.phase.replace('_', ' ')}; no further attempts.` });
  if (paper.onHold) blockers.push({ code: 'ON_HOLD', message: 'Paper is on hold.' });
  if (paper.recalled) blockers.push({ code: 'RECALLED', message: 'Paper has been recalled by the customer.' });

  const cal = calendarBlocks(rules, proposed.at);
  if (cal.length) {
    if (proposed.calendarOverride) {
      warnings.push({ code: 'CALENDAR_OVERRIDE', message: `Supervisor override applied: ${cal.map((c) => c.message).join(' ')}` });
      if (!rules.overrideAttemptsQualify) {
        warnings.push({ code: 'OVERRIDE_NOT_QUALIFYING', message: 'Attempts on blocked days do not count toward non-service.' });
      }
    } else {
      blockers.push(...cal);
    }
  }

  const outsideServiceHours = !isWithinServiceHours(rules, proposed.at);
  if (outsideServiceHours) {
    warnings.push({
      code: 'OUTSIDE_SERVICE_HOURS',
      message: `Outside normal service hours (${rules.serviceHours.start}–${rules.serviceHours.end}). Attempt allowed; explanation optional.`,
    });
  }

  const method = proposed.method ?? null;
  if (method === 'substitute' && !type.substituteAllowed) {
    blockers.push({ code: 'SUBSTITUTE_NOT_PERMITTED', message: `${type.name} does not permit substitute service.` });
  }
  if (method === 'posting') {
    if (type.posting === 'never') {
      blockers.push({ code: 'POSTING_NOT_PERMITTED', message: `${type.name} does not permit posting.` });
    } else if (type.posting === 'after_qualifying_attempts') {
      const prog = evictionProgress(rules, type, paper.attempts);
      const today = localParts(proposed.at, rules.timezone).date;
      if (!prog.postingEligibleFromDate) {
        blockers.push({ code: 'POSTING_TOO_EARLY', message: `Posting requires two qualifying attempts first. ${prog.notes.at(-1) ?? ''}`.trim() });
      } else if (today < prog.postingEligibleFromDate) {
        blockers.push({ code: 'POSTING_TOO_EARLY', message: `Posting / attempt 3 allowed from ${prog.postingEligibleFromDate}.` });
      }
    }
  }

  const outcome = proposed.outcome;
  if (outcome) {
    if (outcome === 'property_documented' && type.completion !== 'property_documentation') {
      blockers.push({ code: 'OUTCOME_NOT_APPLICABLE', message: 'Property documentation applies only to Writ of Execution-type papers.' });
    }
    if (outcome === 'supervisor_action' && type.completion !== 'supervisor_action') {
      blockers.push({ code: 'OUTCOME_NOT_APPLICABLE', message: 'Supervisor-directed action applies only to Writ of Assistance-type papers.' });
    }
    if (outcome === 'served' && !method) {
      blockers.push({ code: 'METHOD_REQUIRED', message: 'A service method is required for a successful service.' });
    }
    if (outcome !== 'served' && method === 'posting') {
      blockers.push({ code: 'POSTING_IS_SERVICE', message: 'Posting is recorded as a completed service.' });
    }
    if (IMMEDIATE_NON_SERVICE_OUTCOMES.includes(outcome) && !type.immediateDispositions.includes(outcome)) {
      blockers.push({ code: 'DISPOSITION_NOT_PERMITTED', message: `Immediate disposition "${outcome}" is not permitted for ${type.name}.` });
    }
  }

  // Qualification warnings (never block — the server may still attempt service).
  if (!outcome || isUnsuccessful(outcome)) {
    const ew = evictionAttempt2Warning(rules, type, paper.attempts, proposed.at);
    if (ew) warnings.push(ew);
    if (type.nonService) {
      const ww = windowWarning(rules, type, paper.attempts, proposed.at);
      if (ww) warnings.push(ww);
    }
  }

  return { allowed: blockers.length === 0, blockers, warnings, outsideServiceHours };
}

// ─── Substitute recipient (RULE-011) ───────────────────────────────────────

export function validateSubstituteRecipient(type: PaperTypeRules, r: SubstituteRecipient | null | undefined): Issue[] {
  if (!type.substituteAllowed) return [{ code: 'SUBSTITUTE_NOT_PERMITTED', message: `${type.name} does not permit substitute service.` }];
  const p = type.substituteRecipient;
  if (!p) return [{ code: 'CONFIG', message: 'Substitute recipient policy missing from rule set.' }];
  if (!r) return [{ code: 'RECIPIENT_REQUIRED', message: 'Substitute recipient details are required.' }];
  const issues: Issue[] = [];
  if (p.requireName && !r.name?.trim()) issues.push({ code: 'RECIPIENT_NAME', message: 'Recipient name is required.' });
  if (p.requireRelationship && !r.relationship?.trim()) issues.push({ code: 'RECIPIENT_RELATIONSHIP', message: 'Relationship to defendant is required.' });
  const age = r.age ?? r.estimatedAge ?? null;
  if (age == null) issues.push({ code: 'RECIPIENT_AGE', message: 'Age or estimated age is required.' });
  else if (age < p.minAge) issues.push({ code: 'RECIPIENT_UNDERAGE', message: `Recipient must be at least ${p.minAge}.` });
  if (p.requireResidesWithDefendant && r.residesWithDefendant !== true)
    issues.push({ code: 'RECIPIENT_RESIDENCE', message: 'Must confirm recipient lives at the address with the defendant.' });
  if (p.requireCompetent && r.competent !== true) issues.push({ code: 'RECIPIENT_COMPETENCE', message: 'Must confirm recipient is of competent mind.' });
  if (p.requireNotAParty && r.notAParty !== true) issues.push({ code: 'RECIPIENT_PARTY', message: 'Must confirm recipient is not a party to the action.' });
  return issues;
}

// ─── Multiple papers at one stop (RULE-015) ────────────────────────────────

export function validateBatchCompletion(
  rules: RuleSetConfig,
  items: { paperId: string; paperTypeCode: string; method: ServiceMethod }[],
): Issue[] {
  const issues: Issue[] = [];
  const methods = new Set(items.map((i) => i.method));
  if (methods.size > 1) {
    issues.push({ code: 'INCOMPATIBLE_METHODS', message: 'Papers with different service methods cannot be batch-completed together.' });
  }
  for (const i of items) {
    const t = getPaperType(rules, i.paperTypeCode);
    if (i.method === 'substitute' && !t.substituteAllowed) {
      issues.push({ code: 'SUBSTITUTE_NOT_PERMITTED', message: `${t.name} (${i.paperId}) does not permit substitute service.` });
    }
    if (i.method === 'posting' && t.posting === 'never') {
      issues.push({ code: 'POSTING_NOT_PERMITTED', message: `${t.name} (${i.paperId}) does not permit posting.` });
    }
  }
  return issues;
}

// ─── Writ of Ejectment set-out lock (RULE-008) ─────────────────────────────

export function setOutSchedulingCheck(
  rules: RuleSetConfig,
  paperTypeCode: string,
  servedAt: Date | null,
  now: Date,
  scheduledFor: Date,
): Issue[] {
  const t = getPaperType(rules, paperTypeCode);
  if (!t.setOut) return [{ code: 'SETOUT_NOT_APPLICABLE', message: `${t.name} has no set-out step.` }];
  if (!servedAt) return [{ code: 'SETOUT_NOT_SERVED', message: 'Writ must be served or posted before a set-out can be scheduled.' }];
  const unlock = new Date(+servedAt + t.setOut.waitHoursAfterService * HOUR_MS);
  const issues: Issue[] = [];
  if (now < unlock) issues.push({ code: 'SETOUT_LOCKED', message: `Set-out is locked until ${unlock.toISOString()} (${t.setOut.waitHoursAfterService}-hour waiting period).` });
  if (scheduledFor < unlock) issues.push({ code: 'SETOUT_TOO_EARLY', message: 'Set-out cannot be scheduled before the waiting period ends.' });
  return issues;
}

// ─── Fees (PAY-001) ────────────────────────────────────────────────────────

export function lookupFeeCents(rules: RuleSetConfig, paperTypeCode: string, customerClass: string): number | null {
  const f = rules.fees.find((x) => x.paperTypeCode === paperTypeCode && x.customerClass === customerClass);
  return f ? f.amountCents : null;
}

// ─── Eligible now / later today / not today (Route Mode possession breakdown) ─
export interface EligibilityToday {
  bucket: 'now' | 'later' | 'notToday';
  /** For 'later': the first time today it opens up (checked in 15-minute steps). */
  from?: Date;
  /** Why it isn't eligible right now. */
  why?: string;
}

/**
 * When today can this paper be served? Uses the same checks as an attempt (service hours, Sundays,
 * holidays, holds) plus, for Rule to Vacate, the 48-hour / 8-hour spacing for attempt 2.
 * `calendarOverrideDate` is a supervisor override for one local date.
 */
export function eligibilityToday(rules: RuleSetConfig, paper: PaperRuleContext, at: Date, calendarOverrideDate?: string | null): EligibilityToday {
  if (paper.phase !== 'open') return { bucket: 'notToday', why: 'Not open for service' };
  const type = getPaperType(rules, paper.paperTypeCode);
  // "Eligible" means an attempt would count: if it wouldn't turn a new M/A/E/W circle green (same window
  // already covered, or too soon after another counted attempt), it's a non-qualified attempt — not eligible.
  const check = (t: Date): string | null => {
    const r = evaluateAttempt(rules, paper, { at: t, calendarOverride: calendarOverrideDate === localParts(t, rules.timezone).date });
    if (!r.allowed) return r.blockers[0]?.message ?? 'Not allowed now';
    if (!isWithinServiceHours(rules, t)) return `Outside service hours (${rules.serviceHours.start}–${rules.serviceHours.end})`;
    const w = windowWarning(rules, type, paper.attempts, t);
    if (w) return `Non-qualified attempt — ${w.message.replace(/^Non-qualified attempt: you can log it, but it will not count toward non-service: /, '').replace(/\. Non-service needs.*$/, '')}`;
    const v = vacateStatus(rules, type, paper.attempts, t);
    return v && !v.eligibleNow ? v.text : null;
  };
  const why = check(at);
  if (why == null) return { bucket: 'now' };
  const day = localParts(at, rules.timezone).date;
  const step = 15 * 60_000;
  for (let t = new Date(Math.ceil((+at + 1) / step) * step); localParts(t, rules.timezone).date === day; t = new Date(+t + step)) {
    if (check(t) == null) return { bucket: 'later', from: t, why };
  }
  return { bucket: 'notToday', why };
}
